"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { message } from "antd";

import UpcomingCard from "../UpcomingCard/UpcomingCard";
import PatientRescheduleModal from "../PatientRescheduleModal/PatientRescheduleModal";
import useRendezvous from "../../hooks/useRendezvous";

import "./UpcomingAppointments.css";

const CHANNEL_NAME = "medilink:appointments";

const notifyDoctorView = (appointmentId, status, extra = {}) => {
  const payload = { appointmentId: String(appointmentId), status, ...extra };

  try {
    const bc = new BroadcastChannel(CHANNEL_NAME);
    bc.postMessage(payload);
    bc.close();
  } catch (e) {
    console.warn("BroadcastChannel not supported, falling back to localStorage");
  }

  try {
    localStorage.setItem(
      "medilink:appointment_action",
      JSON.stringify({ ...payload, _ts: Date.now() })
    );
  } catch (e) {}

  window.dispatchEvent(
    new CustomEvent("appointment:patient_action", { detail: payload })
  );
};

const normalizeId = (value) => {
  if (!value) return "";

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (typeof value === "object") {
    return String(
      value.id ||
        value._id ||
        value.uuid ||
        value.doctorId ||
        value.medecinId ||
        value.providerId ||
        value.userId ||
        ""
    );
  }

  return "";
};

const getDoctorIdFromAppointment = (appointment) => {
  return normalizeId(
    appointment?.doctorId ||
      appointment?.medecinId ||
      appointment?.providerId ||
      appointment?.doctor ||
      appointment?.medecin ||
      appointment?.provider ||
      appointment?.doctorInfo ||
      appointment?.medecinInfo
  );
};

const getDoctorIds = (doctor) => {
  return [
    doctor?.id,
    doctor?._id,
    doctor?.uuid,
    doctor?.doctorId,
    doctor?.medecinId,
    doctor?.providerId,
    doctor?.userId,
    doctor?.authDoctorId,
    doctor?.doctor?.id,
    doctor?.doctor?._id,
    doctor?.user?.id,
    doctor?.user?._id,
  ]
    .filter(Boolean)
    .map(String);
};

const getDoctorFullName = (doctor) => {
  if (!doctor) return "";

  const firstName =
    doctor.firstName ||
    doctor.firstname ||
    doctor.prenom ||
    doctor.first_name ||
    doctor.user?.firstName ||
    doctor.user?.firstname ||
    doctor.user?.prenom ||
    doctor.user?.first_name ||
    "";

  const lastName =
    doctor.lastName ||
    doctor.lastname ||
    doctor.nom ||
    doctor.last_name ||
    doctor.user?.lastName ||
    doctor.user?.lastname ||
    doctor.user?.nom ||
    doctor.user?.last_name ||
    "";

  return (
    doctor.fullName ||
    doctor.fullname ||
    doctor.name ||
    doctor.displayName ||
    doctor.user?.fullName ||
    doctor.user?.fullname ||
    doctor.user?.name ||
    doctor.user?.displayName ||
    `${firstName} ${lastName}`.trim()
  );
};

const getDoctorSpecialty = (doctor) => {
  if (!doctor) return "";

  return (
    doctor.specialty ||
    doctor.specialite ||
    doctor.speciality ||
    doctor.specialtyName ||
    doctor.specialiteName ||
    doctor.user?.specialty ||
    doctor.user?.specialite ||
    ""
  );
};

const enrichAppointmentWithDoctor = (appointment, doctors = []) => {
  const appointmentDoctorId = getDoctorIdFromAppointment(appointment);

  const foundDoctor = doctors.find((doctor) => {
    const ids = getDoctorIds(doctor);
    return appointmentDoctorId && ids.includes(String(appointmentDoctorId));
  });

  if (!foundDoctor) {
    return appointment;
  }

  const doctorName = getDoctorFullName(foundDoctor);
  const doctorSpecialty = getDoctorSpecialty(foundDoctor);

  return {
    ...appointment,

    doctor: appointment?.doctor || foundDoctor,
    medecin: appointment?.medecin || foundDoctor,

    doctorName: appointment?.doctorName || doctorName,
    medecinName: appointment?.medecinName || doctorName,
    providerName: appointment?.providerName || doctorName,
    providerDisplay: appointment?.providerDisplay || doctorName,

    providerTitle:
      appointment?.providerTitle ||
      appointment?.specialtyDisplay ||
      appointment?.specialty ||
      doctorSpecialty ||
      "",
  };
};

const UpcomingAppointments = ({
  refreshKey = 0,
  onAppointmentsChanged,
  doctors = [],
  highlightId,
}) => {
  const { t } = useTranslation();

  const {
    getUpcomingPatientAppointments,
    cancelAppointment,
    rescheduleAppointment,
  } = useRendezvous();

  const [rawAppointments, setRawAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);

  const appointments = useMemo(() => {
    return rawAppointments.map((appointment) =>
      enrichAppointmentWithDoctor(appointment, doctors)
    );
  }, [rawAppointments, doctors]);

  const loadUpcomingAppointments = useCallback(async () => {
    setLoading(true);
    setHasLoaded(false);
    setLocalError(null);

    try {
      const data = await getUpcomingPatientAppointments();
      const list = Array.isArray(data) ? data : [];


      setRawAppointments(list);
    } catch (err) {
      console.error("❌ Failed to load upcoming appointments:", err);
      setLocalError(
        err?.message || "Erreur lors du chargement des rendez-vous."
      );
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [getUpcomingPatientAppointments, doctors]);

  useEffect(() => {
    loadUpcomingAppointments();
  }, [loadUpcomingAppointments, refreshKey]);

  useEffect(() => {
    if (!highlightId || loading || !hasLoaded || localError) return undefined;
    const appointment = appointments.find(
      (item) => String(item?.id ?? item?._id ?? "") === String(highlightId)
    );
    if (!appointment) {
      message.info("This appointment is no longer available.");
      return undefined;
    }

    const timer = window.setTimeout(() => {
      const target = Array.from(
        document.querySelectorAll("[data-appointment-id]")
      ).find(
        (element) => element.dataset.appointmentId === String(highlightId)
      );
      if (!target) {
        message.info("This appointment is no longer available.");
        return;
      }
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [appointments, highlightId, loading, hasLoaded, localError]);

  const handleCancelAppointment = async (appointment) => {
    const rendezvousId = appointment?.id ?? appointment?._id;

    if (!rendezvousId) {
      setLocalError("rendezvousId missing.");
      return;
    }

    setBusyId(rendezvousId);
    setLocalError(null);

    try {
      await cancelAppointment(appointment, { by: "patient" });
      await loadUpcomingAppointments();
      onAppointmentsChanged?.();

      notifyDoctorView(rendezvousId, "cancelled");
    } catch (err) {
      console.error("❌ Cancel patient appointment failed:", err);
      setLocalError(
        err?.message || "Erreur pendant l'annulation du rendez-vous."
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleRescheduleConfirm = async ({ appointment, startTime, endTime }) => {
    const rendezvousId = appointment?.id ?? appointment?._id;

    await rescheduleAppointment({
      appointment,
      startTime,
      endTime,
      by: "patient",
    });

    await loadUpcomingAppointments();
    onAppointmentsChanged?.();

    if (rendezvousId) {
      notifyDoctorView(rendezvousId, "rescheduled", { startTime, endTime });
    }
  };

  if (loading) {
    return (
      <div className="upcoming-appointments-state">
        {t("appointmentss.common.loading", "Loading appointments...")}
      </div>
    );
  }

  if (localError) {
    return (
      <div className="upcoming-appointments-state upcoming-appointments-error">
        <span>{localError}</span>

        <button
          type="button"
          onClick={loadUpcomingAppointments}
          style={{ marginInlineStart: 10 }}
        >
          {t("appointmentss.common.retry", "Retry")}
        </button>
      </div>
    );
  }

  if (!appointments.length) {
    return (
      <div className="upcoming-appointments-state">
        {t("appointmentss.upcoming.empty", "No upcoming appointments.")}
      </div>
    );
  }

  return (
    <>
      <div className="upcoming-appointments-list">
        {appointments.map((appointment, index) => {
          const rendezvousId = appointment?.id ?? appointment?._id;

          return (
            <div
              key={rendezvousId || index}
              data-appointment-id={rendezvousId}
              style={
                String(rendezvousId) === String(highlightId)
                  ? { outline: "3px solid #1677ff", borderRadius: 12 }
                  : undefined
              }
            >
              <UpcomingCard
                appointment={appointment}
                isFirst={index === 0}
                busy={busyId === rendezvousId}
                onReschedule={(appt) => setRescheduleTarget(appt)}
                onCancel={() => handleCancelAppointment(appointment)}
              />
            </div>
          );
        })}
      </div>

      {rescheduleTarget && (
        <PatientRescheduleModal
          appointment={rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
          onReschedule={handleRescheduleConfirm}
        />
      )}
    </>
  );
};

UpcomingAppointments.propTypes = {
  refreshKey: PropTypes.number,
  onAppointmentsChanged: PropTypes.func,
  doctors: PropTypes.array,
  highlightId: PropTypes.string,
};

export default UpcomingAppointments;